import { useGlobalStore } from 'stores/global.js'
import { useFormatting } from 'composables/useFormatting.js'

// Task due/remind timestamps are always stored + transmitted as UTC ISO
// strings. Display must respect the user's own `timezone` + `time_format`
// + `date_format` (users table), not just the browser's locale — mirrors
// the `dateStrInTz` pattern already used inline across the calendar views.
export function useTaskDates() {
  const globalStore = useGlobalStore()
  const { dateFormatMask, formatDateInMask } = useFormatting()

  // `user.timezone` is a Rails-friendly `ActiveSupport::TimeZone` name (e.g.
  // "Karachi", "Eastern Time (US & Canada)") — valid for Rails' own
  // `in_time_zone`, but NOT a real IANA identifier, so passing it straight
  // to `Intl.DateTimeFormat`'s `timeZone` option throws a RangeError ("Karachi"
  // isn't "Asia/Karachi"). Every `Intl` call in this file silently swallows
  // that via try/catch and falls back to a raw UTC-based date — which is
  // exactly why due dates were rendering as an unformatted "2026-08-20"
  // instead of the account's actual date_format. `User#date_timezone`
  // (Ruby) already does the Rails-name -> IANA conversion and is exposed on
  // `UserBlueprint`'s `:current_user` view specifically for this — use that,
  // not the raw `timezone` field.
  function userTz() {
    return globalStore.user?.date_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  }

  function is24Hour() {
    return globalStore.user?.time_format === '24_hours'
  }

  function dateStrInUserTz(isoString) {
    try {
      return new Intl.DateTimeFormat('en-CA', { timeZone: userTz() }).format(new Date(isoString))
    } catch {
      return new Date(isoString).toISOString().split('T')[0]
    }
  }

  function todayStrInUserTz() {
    return dateStrInUserTz(new Date().toISOString())
  }

  // "HH:mm" (24-hour, zero-padded) in the user's timezone — the shape
  // <input type="time"> (TaskTimeField.vue) needs for its v-model. Uses
  // formatToParts rather than a plain formatted string because some
  // locales/browsers render hour12:false's midnight hour as "24" instead
  // of "00".
  function timeStrInUserTz(isoString) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: userTz(), hour: '2-digit', minute: '2-digit', hour12: false,
      }).formatToParts(new Date(isoString))
      const get = (type) => parts.find((p) => p.type === type)?.value || '00'
      const hour = get('hour') === '24' ? '00' : get('hour')
      return `${hour}:${get('minute')}`
    } catch {
      return ''
    }
  }

  // "9:00 AM" / "21:00" depending on the user's time_format
  function formatDueTime(isoString) {
    if (!isoString) return ''
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: userTz(), hour: 'numeric', minute: '2-digit', hour12: !is24Hour(),
      }).format(new Date(isoString))
    } catch {
      return ''
    }
  }

  // "Today" / "Tomorrow" / the account's own date_format (e.g. "20/08/2026"
  // for DD/MM/YYYY, "08/20/2026" for MM/DD/YYYY) for anything further out —
  // same mask DatePicker.vue's input already renders, so a due date reads
  // identically whether it's shown here, in the date picker, or anywhere
  // else in the app. Deliberately not a fixed English "Wed, Aug 20" — that
  // was the original implementation and silently ignored `user.date_format`
  // entirely, showing every account the same month-name-first ordering
  // regardless of their actual preference.
  function formatDueDateLabel(isoString) {
    if (!isoString) return ''
    const dateStr = dateStrInUserTz(isoString)
    const today = todayStrInUserTz()
    const tomorrow = dateStrInUserTz(new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString())
    if (dateStr === today) return 'Today'
    if (dateStr === tomorrow) return 'Tomorrow'
    try {
      return formatDateInMask(isoString, dateFormatMask(globalStore.user?.date_format), userTz())
    } catch {
      return dateStr
    }
  }

  function isOverdue(task) {
    if (!task.due_at || ['completed', 'canceled'].includes(task.status)) return false
    return new Date(task.due_at).getTime() < Date.now()
  }

  // Pure calendar-date arithmetic — no timezone conversion involved (both
  // input and output are 'YYYY-MM-DD' calendar dates), so UTC-anchored Date
  // math is deliberately used here instead of userTz(): the point is to
  // shift a calendar day by N days, not to reinterpret a moment across
  // zones. Used by the checklist's due-date quick-pick dropdown to compute
  // "Tomorrow"/"Next week" from "Today".
  function addDaysToDateStr(dateStr, days) {
    const d = new Date(`${dateStr}T00:00:00Z`)
    d.setUTCDate(d.getUTCDate() + days)
    const pad = (n) => String(n).padStart(2, '0')
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
  }

  // "Mon" / "Tue" / etc. for a plain 'YYYY-MM-DD' calendar date — the quick
  // pick's hint text (e.g. "Tomorrow · Thu").
  function weekdayShort(dateStr) {
    return new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: 'UTC' }).format(new Date(`${dateStr}T00:00:00Z`))
  }

  return {
    userTz, is24Hour, dateStrInUserTz, todayStrInUserTz, timeStrInUserTz,
    formatDueTime, formatDueDateLabel, isOverdue, addDaysToDateStr, weekdayShort,
  }
}
